// src/context/FuncionarioContext.tsx

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { db } from "services/firebase";
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, query } from "firebase/firestore";
import { useAuth } from "./AuthContext";

export interface Funcionario {
  id: string;
  nome: string;
  unidadeName: string;
  dataCadastro: string;
  horaCadastro: string;
  foto: string;
  faceId: string;
}

interface FuncionarioContextType {
  funcionarios: Funcionario[];
  adicionarFuncionario: (f: Omit<Funcionario, "id">) => Promise<void>;
  editarFuncionario: (id: string, nome: string) => Promise<void>;
  removerFuncionario: (id: string) => Promise<void>;
}

const FuncionarioContext = createContext<FuncionarioContextType | undefined>(undefined);

export const FuncionarioProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);

  useEffect(() => {
    if (!user?.uid) {
      setFuncionarios([]);
      return;
    }

    const collectionPath = `clientes/${user.uid}/funcionarios`;
    const q = query(collection(db, collectionPath));

    const unsub = onSnapshot(q, snap => {
      const arr: Funcionario[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setFuncionarios(arr);
    });

    return () => unsub();
  }, [user]);

  const getCollectionRef = () => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return collection(db, `clientes/${user.uid}/funcionarios`);
  };

  const getDocRef = (id: string) => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return doc(db, `clientes/${user.uid}/funcionarios`, id);
  };

  const adicionarFuncionario = async (f: Omit<Funcionario, "id">) => {
    await addDoc(getCollectionRef(), f);
  };

  const editarFuncionario = async (id: string, nome: string) => {
    await updateDoc(getDocRef(id), { nome });
  };

  const removerFuncionario = async (id: string) => {
    await deleteDoc(getDocRef(id));
  };

  return (
    <FuncionarioContext.Provider value={{ funcionarios, adicionarFuncionario, editarFuncionario, removerFuncionario }}>
      {children}
    </FuncionarioContext.Provider>
  );
};

export const useFuncionarios = () => {
  const ctx = useContext(FuncionarioContext);
  if (!ctx) throw new Error("useFuncionarios must be used inside FuncionarioProvider");
  return ctx;
};
